import { batch, computed, effect, signal } from '@preact/signals-react';
import { captureCueboardFeatureOutcomeEvent } from '@/utils/posthog';
import { createRandomID } from '@/utils/random-id';
import {
  HTTPStatusError,
  isAgentDBMigratingError,
  type WebAgentConversationInfo,
  type WebChatBootstrap,
  type WebConversationListItem,
} from '@/website/lib/chat-api';
import { ActivityLiveDataset } from './activity-live-dataset';
import { CapabilityRequestLiveDataset } from './capability-request-live-dataset';
import { ConversationCollection } from './conversation-collection';
import {
  BRIDGE_CHAT_ONBOARDING_CONVERSATION_ID,
  FIXED_BRIDGE_CHAT_TITLE,
} from './conversation-labels';
import { ConversationLiveDataset } from './conversation-live-dataset';
import { ConversationRoute } from './conversation-route';
import { normalizeText } from './conversation-utils';
import type {
  ConversationCreateOptions,
  ConversationCoreDeleteResult,
  ConversationCoreOpenResult,
  ConversationDomainModelOptions,
  ConversationRuntimeRef,
  ConversationSendDraft,
  ConversationSendResult,
} from './types';

type RefreshConversationListOptions = {
  limit?: number;
  cursor?: string | null;
  restartStream?: boolean;
  silent?: boolean;
};

function defaultIdFactory() {
  return createRandomID();
}

function defaultNow() {
  return Date.now() / 1000;
}

function messageContentBlockCount(content: ConversationSendDraft['content']) {
  return Array.isArray(content) ? content.length : 1;
}

function statusCodeFromError(error: unknown) {
  return error instanceof HTTPStatusError ? error.status : 0;
}

function featureOutcomeFailureClass(error: unknown) {
  const statusCode = statusCodeFromError(error);
  if (statusCode === 0) return 'client.network_error';
  if (statusCode === 401 || statusCode === 403) return 'client.unauthorized';
  if (statusCode === 408 || statusCode === 499) return 'client.network_error';
  if (statusCode === 429) return 'client.rate_limited';
  if (statusCode >= 400 && statusCode < 500) {
    return 'client.request_rejected';
  }
  if (statusCode === 502 || statusCode === 503 || statusCode === 504) {
    return 'runtime.backend_unreachable';
  }
  return 'runtime.backend_error';
}

function captureMessageSendOutcome({
  agentGroupId,
  clientRequestId,
  conversationId,
  draft,
  error,
  localMessageId,
  runtimeRef,
  startedAt,
  success,
}: {
  agentGroupId: string;
  clientRequestId: string;
  conversationId: string;
  draft: ConversationSendDraft;
  error?: unknown;
  localMessageId?: string;
  runtimeRef: ConversationRuntimeRef | null;
  startedAt: number;
  success: boolean;
}) {
  captureCueboardFeatureOutcomeEvent({
    featureKey: 'agent.message_send',
    surface: 'website_chat',
    outcome: success ? 'success' : 'failure',
    operationId: clientRequestId,
    requestId: clientRequestId,
    sessionId: runtimeRef?.sessionId,
    agentId: runtimeRef?.agentId,
    statusCode: success ? 200 : statusCodeFromError(error),
    durationMs: Math.max(0, Date.now() - startedAt),
    failureClass: success ? undefined : featureOutcomeFailureClass(error),
    error,
    properties: {
      conversation_id: conversationId,
      agent_group_id: agentGroupId,
      local_message_id: localMessageId || '',
      message_kind: draft.kind || '',
      content_block_count: messageContentBlockCount(draft.content),
      suspect_layer: success ? '' : 'willow_proxy',
    },
  });
}

function isEmptyOutboundContent(content: ConversationSendDraft['content']) {
  if (typeof content === 'string') return content.trim().length === 0;
  return content.length === 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function metadataWithClientRequestId(
  metadata: unknown,
  clientRequestId: string
): unknown {
  if (!clientRequestId) return metadata;
  const base = isRecord(metadata) ? { ...metadata } : {};
  const client = isRecord(base.client) ? { ...base.client } : {};
  return {
    ...base,
    client_request_id: clientRequestId,
    client: {
      ...client,
      request_id: clientRequestId,
    },
  };
}

function isHTTPStatus(error: unknown, status: number) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === status
  );
}

function listItemFromConversation(
  conversation: WebAgentConversationInfo
): WebConversationListItem {
  return {
    conversationId: conversation.conversationId,
    agentGroupId: conversation.agentGroupId,
    kind: conversation.kind,
    title: conversation.title,
    createdByAgentId: conversation.createdByAgentId,
    createdBySessionId: conversation.createdBySessionId,
    participants: conversation.participants ?? [],
    messageCount: conversation.messageCount,
    targetMessageCount: conversation.targetMessageCount,
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt,
  };
}

export class ConversationDomainModel {
  readonly conversationData = new ConversationLiveDataset();
  readonly activityData = new ActivityLiveDataset();
  readonly capabilityData = new CapabilityRequestLiveDataset();
  readonly bootstrap = signal<WebChatBootstrap | null>(null);
  readonly bootstrapLoading = signal(false);
  readonly bootstrapError = signal<unknown | null>(null);
  readonly currentUser = computed(() => this.bootstrap.value?.user ?? null);
  readonly currentUserCredits = computed(
    () => this.bootstrap.value?.credits ?? null
  );
  readonly agentGroupId = this.conversationData.agentGroupId;
  readonly conversations = new ConversationCollection(
    this.conversationData,
    this.activityData
  );
  readonly route = new ConversationRoute();
  readonly routeAgentGroupId = signal<string | null>(null);
  readonly routeConversationId = this.route.conversationId;
  readonly selectedConversationId = this.route.conversationId;
  readonly selectedConversationRecord = computed(() =>
    this.conversations.getRecord(this.selectedConversationId.value)
  );
  readonly selectedListConversationId = computed(() => {
    const selectedConversationId = this.selectedConversationId.value;
    return this.conversations.listConversationIdFor(selectedConversationId);
  });
  readonly selectedListConversationRecord = computed(() =>
    this.conversations.getRecord(this.selectedListConversationId.value)
  );
  readonly selectedConversation = computed(() =>
    this.selectedConversationRecord.value &&
    !this.selectedConversationRecord.value.deleted.value &&
    this.selectedConversationRecord.value.hasInfo.value
      ? this.selectedConversationRecord.value.info.value
      : null
  );
  readonly selectedListConversation = computed(() =>
    this.selectedListConversationRecord.value &&
    !this.selectedListConversationRecord.value.deleted.value &&
    this.selectedListConversationRecord.value.hasInfo.value
      ? this.selectedListConversationRecord.value.info.value
      : null
  );
  readonly selectedListDeleted = computed(
    () => this.selectedListConversationRecord.value?.deleted.value ?? false
  );
  readonly selectedMessageThread = computed(() => {
    const conversation = this.selectedConversationRecord.value;
    return conversation && !conversation.deleted.value
      ? conversation.messages
      : null;
  });
  readonly selectedRuntimeRefs = computed(() => {
    const conversation = this.selectedConversationRecord.value;
    return conversation &&
      !conversation.deleted.value &&
      conversation.hasInfo.value
      ? conversation.runtimeRefs.value
      : [];
  });
  readonly selectedRuntimeRef = computed(() => {
    const refs = this.selectedRuntimeRefs.value;
    return refs.length === 1 ? refs[0] : null;
  });
  readonly conversationListRefreshing =
    this.conversationData.conversationListRefreshing;
  readonly conversationListHydrated =
    this.conversationData.conversationListHydrated;
  readonly loadingConversationList = computed(
    () =>
      this.conversationListRefreshing.value &&
      !this.conversationListHydrated.value
  );
  readonly loadingSelectedMessages = computed(
    () => this.selectedMessageThread.value?.visibleLoading.value ?? false
  );
  readonly refreshingSelectedMessages = computed(
    () => this.selectedMessageThread.value?.snapshotRefreshing.value ?? false
  );
  readonly isSubmittingMessage = signal(false);
  readonly conversationListNextCursor =
    this.conversationData.conversationListNextCursor;
  readonly hasMoreConversationList =
    this.conversationData.hasMoreConversationList;
  readonly lastError = this.conversationData.lastError;
  readonly pendingCapabilityRequests = this.capabilityData.pendingRequests;

  readonly conversationsById = this.conversations.byId;
  readonly messagesByConversationId =
    this.conversations.messagesByConversationId;
  readonly deletedConversationIds = this.conversations.deletedIds;
  readonly conversationList = this.conversations.list;
  readonly routerConversationId = signal<string | null>(null);
  readonly routerConversationLoading = signal(false);
  readonly routerConversationUnavailable = signal(false);
  readonly conversationListItems = this.conversations.listItems;
  readonly isBridgeChatVirtualOnboardingSelected = computed(
    () =>
      this.selectedConversationId.value ===
        BRIDGE_CHAT_ONBOARDING_CONVERSATION_ID &&
      !this.routerConversationId.value &&
      this.routerConversationUnavailable.value
  );
  readonly isBridgeChatSelected = computed(() => {
    const selectedConversationId = this.selectedConversationId.value;
    if (!selectedConversationId) return false;
    if (this.isBridgeChatVirtualOnboardingSelected.value) return true;
    return selectedConversationId === this.routerConversationId.value;
  });
  readonly isBridgeChatOnboardingSelected = computed(() => {
    if (this.isBridgeChatVirtualOnboardingSelected.value) return true;
    const selectedConversationId = this.selectedConversationId.value;
    const routerConversationId = this.routerConversationId.value;
    if (
      !selectedConversationId ||
      selectedConversationId !== routerConversationId
    ) {
      return false;
    }
    const conversation = this.selectedConversationRecord.value;
    const thread = this.selectedMessageThread.value;
    return Boolean(
      conversation &&
      !conversation.deleted.value &&
      conversation.hasInfo.value &&
      thread?.snapshotHydrated.value &&
      !thread.snapshotRefreshing.value &&
      conversation.info.value.messageCount === 0 &&
      thread.messages.value.length === 0
    );
  });
  readonly routerConversationListItem = computed(() => {
    const routerConversationId = this.routerConversationId.value;
    if (!routerConversationId) {
      const agentGroupId = this.agentGroupId.value;
      return this.routerConversationUnavailable.value && agentGroupId
        ? {
            conversationId: BRIDGE_CHAT_ONBOARDING_CONVERSATION_ID,
            agentGroupId,
            kind: 'fixed_bridge_chat',
            title: FIXED_BRIDGE_CHAT_TITLE,
            createdByAgentId: null,
            createdBySessionId: null,
            createdAt: 0,
            updatedAt: 0,
            messageCount: 0,
            targetMessageCount: 0,
            participants: [],
          }
        : null;
    }
    const record = this.conversations.getRecord(routerConversationId);
    return record &&
      !record.deleted.value &&
      record.hasInfo.value &&
      record.isListed.value
      ? { ...record.listItem.value, title: FIXED_BRIDGE_CHAT_TITLE }
      : null;
  });
  readonly historyConversationListItems = computed(() => {
    const routerConversationId = this.routerConversationId.value;
    return routerConversationId
      ? this.conversationListItems.value.filter(
          conversation => conversation.conversationId !== routerConversationId
        )
      : this.conversationListItems.value;
  });
  readonly nextConversationId = computed(() => {
    const selectedListConversationId = this.selectedListConversationId.value;
    return (
      this.historyConversationListItems.value.find(
        conversation =>
          conversation.conversationId !== selectedListConversationId
      )?.conversationId ?? null
    );
  });
  readonly fallbackRouteConversationId = computed(() => {
    if (this.selectedConversationId.value) return null;
    if (this.routerConversationLoading.value) return null;
    return (
      this.routerConversationId.value ??
      this.historyConversationListItems.value[0]?.conversationId ??
      null
    );
  });
  readonly conversationListItemsById = computed(() => {
    const next = new Map<string, WebConversationListItem>();
    for (const conversation of this.conversationListItems.value) {
      next.set(conversation.conversationId, conversation);
    }
    const routerConversation = this.routerConversationListItem.value;
    if (routerConversation) {
      next.set(routerConversation.conversationId, routerConversation);
    }
    const selectedListConversation = this.selectedListConversation.value;
    if (
      selectedListConversation &&
      !next.has(selectedListConversation.conversationId)
    ) {
      next.set(
        selectedListConversation.conversationId,
        listItemFromConversation(selectedListConversation)
      );
    }
    return next;
  });
  readonly selectedRouteInfoPending = computed(() =>
    Boolean(
      this.selectedConversationId.value &&
      !this.isBridgeChatVirtualOnboardingSelected.value &&
      !this.selectedConversationRecord.value?.hasInfo.value
    )
  );
  readonly selectedRouteConversationListItem = computed(() => {
    const selectedConversationId = this.selectedConversationId.value;
    return selectedConversationId
      ? (this.conversationListItemsById.value.get(selectedConversationId) ??
          null)
      : null;
  });
  readonly selectedConversationListItem = computed(
    () => this.selectedRouteConversationListItem.value
  );
  readonly participantsByConversationId =
    this.conversations.participantsByConversationId;
  readonly isTaskConversationSelected = computed(() => {
    const conversation = this.selectedConversationRecord.value;
    if (
      conversation &&
      !conversation.deleted.value &&
      conversation.hasInfo.value
    ) {
      return conversation.isTask.value;
    }
    return false;
  });
  readonly isSelectedConversationReadOnly = computed(() => {
    if (this.isBridgeChatOnboardingSelected.value) {
      return false;
    }
    const conversation = this.selectedConversationRecord.value;
    return (
      conversation?.isReadOnly.value ??
      Boolean(this.selectedConversationId.value)
    );
  });
  readonly selectedConversationCanAcceptUserMessage = computed(
    () =>
      this.selectedConversationRecord.value?.canAcceptUserMessage.value ?? false
  );
  readonly selectedRawActivities = computed(
    () =>
      this.selectedConversationRecord.value?.activity.rawActivities.value ?? []
  );
  readonly selectedParticipantActivities = computed(
    () =>
      this.selectedConversationRecord.value?.activity.participantActivities
        .value ?? []
  );
  readonly displayedAssistantActivities = computed(
    () =>
      this.selectedConversationRecord.value?.activity.displayedAssistantActivities
        .value ?? []
  );
  readonly hasActiveAssistantActivity = computed(
    () =>
      this.selectedConversationRecord.value?.activity.hasActiveAssistantActivity
        .value ?? false
  );
  readonly selectedLocalProcessing = computed(
    () => this.selectedConversationRecord.value?.localProcessing.value ?? false
  );
  readonly isProcessing = computed(
    () =>
      this.selectedLocalProcessing.value ||
      this.hasActiveAssistantActivity.value
  );
  readonly selectedMessages = computed(
    () => this.selectedMessageThread.value?.messages.value ?? []
  );
  readonly canSubmitSelectedConversation = computed(() => {
    return Boolean(
      this.agentGroupId.value &&
      ((this.selectedConversationId.value &&
        this.selectedConversationCanAcceptUserMessage.value) ||
        this.isBridgeChatOnboardingSelected.value) &&
      !this.isSubmittingMessage.value
    );
  });
  readonly selectedAgentGroupIdForCommands = computed(
    () =>
      this.agentGroupId.value ??
      this.selectedConversation.value?.agentGroupId ??
      this.selectedConversationListItem.value?.agentGroupId ??
      null
  );
  readonly selectedConversationTitle = computed(() => {
    if (this.isTaskConversationSelected.value) {
      return (
        this.selectedConversation.value?.title.trim() || 'Task conversation'
      );
    }
    const selectedListItem = this.selectedConversationListItem.value;
    if (selectedListItem) {
      return selectedListItem.title;
    }
    return this.selectedConversationId.value ? '' : 'New conversation';
  });
  readonly selectedShareConversationId = computed(() =>
    this.isSelectedConversationReadOnly.value
      ? null
      : this.selectedListConversationId.value
  );
  readonly selectedConversationLoading = computed(() =>
    Boolean(
      this.selectedConversationId.value &&
      !this.isBridgeChatVirtualOnboardingSelected.value &&
      (this.selectedRouteInfoPending.value ||
        !this.selectedConversationRecord.value?.hasInfo.value ||
        this.loadingSelectedMessages.value)
    )
  );

  private readonly transport: ConversationDomainModelOptions['transport'];
  private readonly idFactory: () => string;
  private readonly now: () => number;
  private readonly defaultConversationLimit: number;
  private readonly defaultMessageLimit: number;
  private readonly started = signal(false);
  private bootstrapPromise: Promise<WebChatBootstrap | null> | null = null;
  private bootstrapRequestId = 0;
  private lastHandledConversationOpenCount = 0;
  private selectedSnapshotRequestKey: string | null = null;
  private readonly disposeStreamLifecycleEffect: () => void;
  private readonly disposeConversationOpenEffect: () => void;
  private readonly disposeSelectedSnapshotEffect: () => void;

  constructor(options: ConversationDomainModelOptions) {
    this.transport = options.transport;
    this.idFactory = options.idFactory ?? defaultIdFactory;
    this.now = options.now ?? defaultNow;
    this.defaultConversationLimit = options.defaultConversationLimit ?? 50;
    this.defaultMessageLimit = options.defaultMessageLimit ?? 200;
    this.conversationData.configure(
      options.transport,
      this.defaultConversationLimit,
      this.defaultMessageLimit
    );
    this.activityData.configure(options.transport);
    this.capabilityData.configure(options.transport);
    this.disposeStreamLifecycleEffect = effect(() => {
      const started = this.started.value;
      const agentGroupId = this.agentGroupId.value;
      if (!started || !agentGroupId) {
        this.stopStreams();
        return;
      }
      this.startStreams();
    });
    this.disposeConversationOpenEffect = effect(() => {
      const openCount = this.conversationData.openCount.value;
      if (openCount <= this.lastHandledConversationOpenCount) return;
      const isReconnect = openCount > 1;
      if (isReconnect) {
        this.conversations.markMessageSnapshotsStaleExcept(
          this.selectedConversationId.peek()
        );
      }
      this.lastHandledConversationOpenCount = openCount;
    });
    this.disposeSelectedSnapshotEffect = effect(() => {
      const agentGroupId = this.agentGroupId.value;
      const selectedConversationId = this.selectedConversationId.value;
      const openCount = this.conversationData.openCount.value;
      if (
        !agentGroupId ||
        !selectedConversationId ||
        this.isBridgeChatVirtualOnboardingSelected.value ||
        openCount < 1
      ) {
        return;
      }

      const requestKey = [
        agentGroupId,
        String(openCount),
        selectedConversationId,
      ].join('\u001f');
      if (this.selectedSnapshotRequestKey === requestKey) return;
      this.selectedSnapshotRequestKey = requestKey;
      void this.refreshSelectedConversationSnapshotAfterOpen(
        selectedConversationId,
        { isReconnect: openCount > 1 }
      ).catch(error => {
        this.lastError.value = error;
      });
    });
  }

  ensureStarted() {
    if (this.started.peek()) return;
    this.started.value = true;
    void this.refreshBootstrap().catch(() => {});
  }

  async refreshBootstrap() {
    if (!this.transport.getBootstrap) return null;
    if (this.bootstrapPromise) return this.bootstrapPromise;

    const requestId = ++this.bootstrapRequestId;
    this.bootstrapLoading.value = true;
    this.bootstrapError.value = null;
    this.bootstrapPromise = this.transport
      .getBootstrap()
      .then(bootstrap => {
        if (requestId !== this.bootstrapRequestId) return this.bootstrap.value;
        batch(() => {
          this.bootstrap.value = bootstrap;
          this.bootstrapError.value = null;
          const bootstrapAgentGroupId = normalizeText(
            bootstrap.agent.agent_group_id
          );
          const targetAgentGroupId =
            this.routeAgentGroupId.peek() ?? bootstrapAgentGroupId;
          this.setAgentGroup(targetAgentGroupId, {
            preserveRoute: true,
          });
          if (targetAgentGroupId === bootstrapAgentGroupId) {
            this.applyBootstrapRouterConversation(
              bootstrap.fixedConversations?.router ?? null,
              bootstrapAgentGroupId
            );
          }
        });
        return bootstrap;
      })
      .catch(error => {
        if (requestId === this.bootstrapRequestId) {
          this.bootstrapError.value = error;
        }
        throw error;
      })
      .finally(() => {
        if (requestId === this.bootstrapRequestId) {
          this.bootstrapLoading.value = false;
          this.bootstrapPromise = null;
        }
      });
    return this.bootstrapPromise;
  }

  setAgentGroup(
    agentGroupId: string | null | undefined,
    options: { preserveRoute?: boolean } = {}
  ) {
    const normalizedAgentGroupId = normalizeText(agentGroupId);
    if (this.agentGroupId.peek() === normalizedAgentGroupId) return;
    this.stopStreams();
    batch(() => {
      this.conversationData.setAgentGroup(normalizedAgentGroupId);
      this.activityData.reset();
      this.capabilityData.setAgentGroup(normalizedAgentGroupId);
      if (!options.preserveRoute) {
        this.route.reset();
      }
      this.conversations.reset();
      this.isSubmittingMessage.value = false;
      this.routerConversationId.value = null;
      this.routerConversationLoading.value = false;
      this.routerConversationUnavailable.value = false;
    });
  }

  setRoute(
    conversationId: string | null | undefined,
    options: { agentGroupId?: string | null } = {}
  ) {
    const routeAgentGroupId = normalizeText(options.agentGroupId);
    batch(() => {
      this.routeAgentGroupId.value = routeAgentGroupId;
      if (routeAgentGroupId && this.agentGroupId.peek() !== routeAgentGroupId) {
        this.setAgentGroup(routeAgentGroupId, { preserveRoute: true });
      }
      this.route.set(conversationId);
    });
  }

  async refreshConversationList(options: RefreshConversationListOptions = {}) {
    await this.conversationData.refreshConversationList({
      ...options,
      getSelectedConversationId: () => this.selectedConversationId.value,
      getSelectedListConversationId: () =>
        this.selectedListConversationId.value,
    });
  }

  async openConversation(
    conversationId: string
  ): Promise<ConversationCoreOpenResult | null> {
    const normalizedConversationId = normalizeText(conversationId);
    if (!this.agentGroupId.value || !normalizedConversationId) return null;

    this.lastError.value = null;
    try {
      const conversation = await this.conversationData.getConversation(
        normalizedConversationId
      );
      if (!conversation) return null;
      this.route.set(conversation.conversationId);
      await this.ensureConversationMessagesHydrated(
        conversation.conversationId
      );
      return {
        conversation,
        messages:
          this.conversations.getRecord(conversation.conversationId)?.messages
            .messages.value ?? [],
      };
    } catch (error) {
      if (isHTTPStatus(error, 404)) {
        this.conversationData.deleteConversation(normalizedConversationId);
        if (this.selectedConversationId.value === normalizedConversationId) {
          this.route.reset();
        }
        void this.refreshConversationList({
          restartStream: false,
          silent: true,
        }).catch(refreshError => {
          this.lastError.value = refreshError;
        });
      }
      this.lastError.value = error;
      throw error;
    }
  }

  async createConversation(options: ConversationCreateOptions = {}) {
    this.lastError.value = null;
    try {
      const conversation = await this.transport.createConversation(
        options.title ?? ''
      );
      if (!this.agentGroupId.value && conversation.agentGroupId) {
        this.setAgentGroup(conversation.agentGroupId, { preserveRoute: true });
      }
      batch(() => {
        this.conversationData.upsertConversation(conversation, {
          mutationSource: 'local',
        });
        if (this.conversationData.openCount.peek() > 0) {
          this.conversationData.setMessages(conversation.conversationId, []);
          this.conversations
            .ensureShell(conversation.conversationId)
            ?.messages.markSnapshotHydrated();
        }
        if (options.select ?? true) {
          this.route.set(conversation.conversationId);
        }
      });
      return conversation;
    } catch (error) {
      this.lastError.value = error;
      throw error;
    }
  }

  async ensureBridgeChatConversation(): Promise<WebAgentConversationInfo | null> {
    if (!this.transport.ensureBridgeChatConversation) {
      return null;
    }
    this.lastError.value = null;
    try {
      const conversation = await this.transport.ensureBridgeChatConversation();
      if (!this.agentGroupId.value && conversation.agentGroupId) {
        this.setAgentGroup(conversation.agentGroupId, { preserveRoute: true });
      }
      this.applyBootstrapRouterConversation(
        conversation,
        conversation.agentGroupId ?? this.agentGroupId.value
      );
      this.route.set(conversation.conversationId);
      return conversation;
    } catch (error) {
      this.lastError.value = error;
      throw error;
    }
  }

  async refreshActivities() {
    try {
      return await this.activityData.refresh();
    } catch (error) {
      this.lastError.value = error;
      throw error;
    }
  }

  async loadSelectedMessages(options: { force?: boolean } = {}) {
    const agentGroupId = this.agentGroupId.value;
    const selectedConversationId = this.selectedConversationId.value;
    if (!agentGroupId || !selectedConversationId) {
      return;
    }

    await this.ensureConversationMessagesHydrated(
      selectedConversationId,
      options
    );
  }

  async sendSelectedConversationMessage(
    draft: ConversationSendDraft
  ): Promise<ConversationSendResult> {
    if (this.isBridgeChatVirtualOnboardingSelected.value) {
      if (isEmptyOutboundContent(draft.content)) return { status: 'empty' };
      try {
        const conversation = await this.ensureBridgeChatConversation();
        const conversationId = normalizeText(conversation?.conversationId);
        if (!conversationId) return { status: 'missing_target' };
        return this.sendConversationMessage(conversationId, draft);
      } catch (error) {
        this.lastError.value = error;
        return { status: 'failed', error };
      }
    }

    let conversationId = this.selectedConversationId.value;
    if (!conversationId) {
      try {
        const created = await this.createConversation({ select: true });
        conversationId = normalizeText(created.conversationId);
      } catch (error) {
        this.lastError.value = error;
        return { status: 'failed', error };
      }
    }
    if (!conversationId) return { status: 'missing_target' };
    return this.sendConversationMessage(conversationId, draft);
  }

  runtimeRefForConversation(
    conversationId: string | null | undefined
  ): ConversationRuntimeRef | null {
    const refs = this.runtimeRefsForConversation(conversationId);
    return refs.length === 1 ? refs[0] : null;
  }

  runtimeRefsForConversation(
    conversationId: string | null | undefined
  ): ConversationRuntimeRef[] {
    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return [];
    const conversation = this.conversations.getRecord(normalizedConversationId);
    return conversation &&
      !conversation.deleted.value &&
      conversation.hasInfo.value
      ? conversation.runtimeRefs.value
      : [];
  }

  async sendConversationMessage(
    conversationId: string,
    draft: ConversationSendDraft
  ): Promise<ConversationSendResult> {
    if (isEmptyOutboundContent(draft.content)) return { status: 'empty' };

    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return { status: 'missing_target' };

    const conversation = this.conversations.getRecord(normalizedConversationId);
    const agentGroupId =
      this.agentGroupId.value ??
      normalizeText(conversation?.info.value.agentGroupId);
    if (!agentGroupId) return { status: 'missing_target' };
    if (!conversation?.canAcceptUserMessage.value) {
      return { status: 'read_only' };
    }
    const thread = conversation.messages;

    const localContent = thread.localContentFromOutbound(
      draft.content,
      draft.localContent
    );
    const clientRequestId = draft.clientRequestId ?? this.idFactory();
    const metadata = metadataWithClientRequestId(
      draft.metadata,
      clientRequestId
    );
    const localMessageId = localContent.length > 0 ? this.idFactory() : '';
    const localMessage =
      localContent.length > 0
        ? thread.appendLocalUserMessage(
            localMessageId,
            this.now(),
            localContent,
            metadata
          )
        : null;
    const runtimeRef = conversation.runtimeRef.value;
    const outcomeStartedAt = Date.now();

    batch(() => {
      this.isSubmittingMessage.value = true;
      conversation.startLocalProcessing();
      this.lastError.value = null;
    });

    try {
      const response = await this.transport.sendMessage(
        normalizedConversationId,
        draft.content,
        {
          agentGroupId,
          clientRequestId,
          kind: draft.kind,
          metadata,
        }
      );
      if (localMessage && response.messageId) {
        thread.confirmLocalUserMessage(localMessage.id, response.messageId);
      }
      captureMessageSendOutcome({
        agentGroupId,
        clientRequestId,
        conversationId: normalizedConversationId,
        draft,
        localMessageId: localMessage?.id,
        runtimeRef,
        startedAt: outcomeStartedAt,
        success: true,
      });
      return {
        status: 'sent',
        messageId: response.messageId,
        localMessageId: localMessage?.id,
      };
    } catch (error) {
      if (localMessage && !isAgentDBMigratingError(error)) {
        thread.remove(localMessage.id);
      }
      captureMessageSendOutcome({
        agentGroupId,
        clientRequestId,
        conversationId: normalizedConversationId,
        draft,
        error,
        localMessageId: localMessage?.id,
        runtimeRef,
        startedAt: outcomeStartedAt,
        success: false,
      });
      this.lastError.value = error;
      return {
        status: 'failed',
        error,
        localMessageId: localMessage?.id,
      };
    } finally {
      batch(() => {
        this.isSubmittingMessage.value = false;
        conversation.finishLocalProcessing();
      });
    }
  }

  async renameConversation(conversationId: string, title: string) {
    const agentGroupId = this.agentGroupId.value;
    const normalizedConversationId = normalizeText(conversationId);
    const normalizedTitle = title.trim();
    if (!agentGroupId || !normalizedConversationId || !normalizedTitle) {
      return null;
    }

    const updated = await this.transport.renameConversation(
      normalizedConversationId,
      { agentGroupId, title: normalizedTitle }
    );
    this.conversationData.upsertConversation(updated, {
      mutationSource: 'local',
    });
    return updated;
  }

  async deleteConversation(
    conversationId: string
  ): Promise<ConversationCoreDeleteResult | null> {
    const agentGroupId = this.agentGroupId.value;
    const normalizedConversationId = normalizeText(conversationId);
    if (!agentGroupId || !normalizedConversationId) return null;
    await this.transport.deleteConversation(normalizedConversationId, {
      agentGroupId,
    });
    const deletedSelectedConversation =
      this.selectedListConversationId.value === normalizedConversationId;
    this.conversationData.deleteConversation(normalizedConversationId);
    return {
      conversationId: normalizedConversationId,
      deletedSelectedConversation,
      nextConversationId: this.nextConversationId.value,
    };
  }

  async shareSelectedConversation() {
    const agentGroupId = this.agentGroupId.value;
    const conversation = this.selectedConversation.value;
    if (!agentGroupId || !conversation) return null;
    return this.transport.shareConversation(conversation.conversationId, {
      agentGroupId,
      title: conversation.title,
    });
  }

  async shareLocationCapabilityRequest(requestId: string, payload: unknown) {
    const agentGroupId = this.selectedAgentGroupIdForCommands.value;
    const normalizedRequestId = normalizeText(requestId);
    if (!agentGroupId || !normalizedRequestId) return null;

    const request = await this.transport.shareLocationCapabilityRequest(
      agentGroupId,
      normalizedRequestId,
      payload
    );
    if (request) {
      this.capabilityData.apply(request);
    }
    return request;
  }

  async confirmOAuthAuthorizationCapabilityRequest(requestId: string) {
    const agentGroupId = this.selectedAgentGroupIdForCommands.value;
    const normalizedRequestId = normalizeText(requestId);
    if (!agentGroupId || !normalizedRequestId) return null;

    const request =
      await this.transport.confirmOAuthAuthorizationCapabilityRequest(
        agentGroupId,
        normalizedRequestId
      );
    if (request) {
      this.capabilityData.apply(request);
    }
    return request;
  }

  async completeInteractiveLoginCapabilityRequest(requestId: string) {
    const agentGroupId = this.selectedAgentGroupIdForCommands.value;
    const normalizedRequestId = normalizeText(requestId);
    if (!agentGroupId || !normalizedRequestId) return null;

    const request =
      await this.transport.completeInteractiveLoginCapabilityRequest(
        agentGroupId,
        normalizedRequestId
      );
    if (request) {
      this.capabilityData.apply(request);
    }
    return request;
  }

  async decideHostAccessCapabilityRequest(
    requestId: string,
    decision: { approved: boolean }
  ) {
    const agentGroupId = this.selectedAgentGroupIdForCommands.value;
    const normalizedRequestId = normalizeText(requestId);
    if (!agentGroupId || !normalizedRequestId) return null;

    const request = await this.transport.decideHostAccessCapabilityRequest(
      agentGroupId,
      normalizedRequestId,
      decision
    );
    if (request) {
      this.capabilityData.apply(request);
    }
    return request;
  }

  async decideComputerUseStartCapabilityRequest(
    requestId: string,
    decision: { approved: boolean; mode?: string }
  ) {
    const agentGroupId = this.selectedAgentGroupIdForCommands.value;
    const normalizedRequestId = normalizeText(requestId);
    if (!agentGroupId || !normalizedRequestId) return null;

    const request =
      await this.transport.decideComputerUseStartCapabilityRequest(
        agentGroupId,
        normalizedRequestId,
        decision
      );
    if (request) {
      this.capabilityData.apply(request);
    }
    return request;
  }

  startStreams() {
    const agentGroupId = this.agentGroupId.value;
    if (!agentGroupId) {
      this.stopStreams();
      return;
    }

    this.conversationData.start({
      getSelectedConversationId: () => this.selectedConversationId.value,
      getSelectedListConversationId: () =>
        this.selectedListConversationId.value,
    });
    this.activityData.start({ loadInitial: true });
    this.capabilityData.start();
  }

  stopStreams() {
    this.conversationData.stop();
    this.activityData.stop();
    this.capabilityData.stop();
  }

  resetForTest() {
    this.started.value = false;
    this.stopStreams();
    this.bootstrapRequestId += 1;
    this.bootstrapPromise = null;
    this.lastHandledConversationOpenCount = 0;
    this.selectedSnapshotRequestKey = null;
    batch(() => {
      this.bootstrap.value = null;
      this.bootstrapLoading.value = false;
      this.bootstrapError.value = null;
      this.conversationData.setAgentGroup(null);
      this.capabilityData.setAgentGroup(null);
      this.routeAgentGroupId.value = null;
      this.route.reset();
      this.resetDomainState();
      this.routerConversationId.value = null;
      this.routerConversationLoading.value = false;
      this.routerConversationUnavailable.value = false;
    });
  }

  dispose() {
    this.started.value = false;
    this.stopStreams();
    this.disposeStreamLifecycleEffect();
    this.disposeConversationOpenEffect();
    this.disposeSelectedSnapshotEffect();
    this.conversations.dispose();
  }

  private resetDomainState() {
    this.conversationData.reset();
    this.activityData.reset();
    this.capabilityData.reset();
    this.conversations.reset();
    this.isSubmittingMessage.value = false;
  }

  private applyBootstrapRouterConversation(
    conversation: WebAgentConversationInfo | null,
    agentGroupId: string | null | undefined
  ) {
    const normalizedAgentGroupId = normalizeText(agentGroupId);
    if (
      !normalizedAgentGroupId ||
      this.agentGroupId.value !== normalizedAgentGroupId
    ) {
      return;
    }
    const conversationId = normalizeText(conversation?.conversationId);
    if (!conversation || !conversationId) {
      this.routerConversationId.value = null;
      this.routerConversationLoading.value = false;
      this.routerConversationUnavailable.value = true;
      return;
    }
    this.routerConversationId.value = conversationId;
    this.routerConversationLoading.value = false;
    this.routerConversationUnavailable.value = false;
    this.conversationData.upsertConversation(conversation, {
      mutationSource: 'local',
    });
  }

  private async ensureConversationMessagesHydrated(
    conversationId: string,
    options: { force?: boolean } = {}
  ) {
    const thread = this.conversations.ensureShell(conversationId)?.messages;
    if (!thread) return [];
    return thread.ensureSnapshotHydrated(
      async currentConversationId =>
        this.conversationData.loadMessageSnapshot(currentConversationId),
      options
    );
  }

  private async refreshSelectedConversationSnapshotAfterOpen(
    selectedConversationId: string,
    info: { isReconnect: boolean }
  ) {
    const conversation = this.conversations.getRecord(selectedConversationId);
    if (info.isReconnect || !conversation?.hasInfo.value) {
      try {
        await this.conversationData.getConversation(selectedConversationId);
      } catch (error) {
        if (isHTTPStatus(error, 404)) {
          this.conversationData.deleteConversation(selectedConversationId);
          return;
        }
        throw error;
      }
    }
    if (this.conversations.getRecord(selectedConversationId)?.deleted.value) {
      return;
    }
    await this.ensureConversationMessagesHydrated(selectedConversationId, {
      force: info.isReconnect,
    });
  }
}
